/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Toolbar Plugin
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { $createHeadingNode, $createQuoteNode, $isHeadingNode, HeadingTagType } from '@lexical/rich-text';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $setBlocksType, $isAtNodeEnd } from '@lexical/selection';
import { Button, Divider, Space } from 'antd';
import {
	CAN_REDO_COMMAND,
	CAN_UNDO_COMMAND,
	REDO_COMMAND,
	UNDO_COMMAND,
	SELECTION_CHANGE_COMMAND,
	FORMAT_TEXT_COMMAND,
	FORMAT_ELEMENT_COMMAND,
	$getSelection,
	$isRangeSelection,
	$createParagraphNode,
	RangeSelection,
	LexicalEditor,
} from 'lexical';
import {
	UndoOutlined,
	RedoOutlined,
	BoldOutlined,
	ItalicOutlined,
	UnderlineOutlined,
	StrikethroughOutlined,
	UnorderedListOutlined,
	OrderedListOutlined,
	LinkOutlined,
	AlignLeftOutlined,
	AlignRightOutlined,
	AlignCenterOutlined,
	DownOutlined,
	MenuOutlined,
	CodeOutlined,
	MessageOutlined,
} from '@ant-design/icons';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND, $isListNode, ListNode } from '@lexical/list';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import H1 from 'assets/lexicalEditor/type-h1.svg';
import H2 from 'assets/lexicalEditor/type-h2.svg';
import TextParagraph from 'assets/lexicalEditor/text-paragraph.svg';
import Quote from 'assets/lexicalEditor/chat-square-quote.svg';
import FloatingLinkEditor from './FloatingLinkEditor';
interface IOptionsDropdown {
	editor: LexicalEditor;
	blockType: string;
	toolbarRef: AnyType;
	setShowBlockOptionsDropDown: (val: boolean) => void;
}

interface BlockTypeToIcon {
	[key: string]: React.ReactNode;
}

const LowPriority = 1;
export const toolbarItem = 'toolbar-item';
export const active = '-active';

const supportedBlockTypes = new Set(['paragraph', 'quote', 'code', 'h1', 'h2', 'ul', 'ol']);

const blockTypeToBlockName = {
	code: 'Code Block',
	h1: 'Large',
	h2: 'Medium',
	ol: 'Numbered List',
	paragraph: 'Normal',
	quote: 'Quote',
	ul: 'Bulleted List',
};
// todo get icons from iconpark library
const blockTypeToIcon: BlockTypeToIcon = {
	paragraph: <img src={TextParagraph} alt='Paragraph' />,
	h1: <img src={H1} alt='H1' />,
	h2: <img src={H2} alt='H2' />,
	quote: <img src={Quote} alt='Quote' />,
};
const getSelectedNode = (selection: RangeSelection) => {
	const anchor = selection.anchor;
	const focus = selection.focus;
	const anchorNode = selection.anchor.getNode();
	const focusNode = selection.focus.getNode();
	if (anchorNode === focusNode) {
		return anchorNode;
	}
	const isBackward = selection.isBackward();
	if (isBackward) {
		return $isAtNodeEnd(focus) ? anchorNode : focusNode;
	} else {
		return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
	}
};

const BlockOptionsDropdownList = ({ editor, blockType, toolbarRef, setShowBlockOptionsDropDown }: IOptionsDropdown) => {
	const dropDownRef = useRef<AnyType>(null);
	useEffect(() => {
		const toolbar = toolbarRef.current;
		const dropDown = dropDownRef.current;

		if (toolbar !== null && dropDown !== null) {
			const { top, left, height } = toolbar.getBoundingClientRect();
			dropDown.style.position = 'fixed';
			dropDown.style.top = `${top + height}px`;
			dropDown.style.left = `${left}px`;
		}
	}, [dropDownRef, toolbarRef]);

	useEffect(() => {
		const dropDown = dropDownRef.current;
		const toolbar = toolbarRef.current;

		if (dropDown !== null && toolbar !== null) {
			const handle = (event: MouseEvent) => {
				const target = event.target;

				if (!dropDown.contains(target) && !toolbar.contains(target)) {
					setShowBlockOptionsDropDown(false);
				}
			};
			document.addEventListener('click', handle);

			return () => {
				document.removeEventListener('click', handle);
			};
		}
	}, [dropDownRef, setShowBlockOptionsDropDown, toolbarRef]);

	const formatParagraph = () => {
		if (blockType !== 'paragraph') {
			editor.update(() => {
				const selection = $getSelection();

				if ($isRangeSelection(selection)) {
					$setBlocksType(selection, () => $createParagraphNode());
				}
			});
		}
		setShowBlockOptionsDropDown(false);
	};

	const formatHeading = (type: HeadingTagType) => {
		if (blockType !== type) {
			editor.update(() => {
				const selection = $getSelection();

				if ($isRangeSelection(selection)) {
					$setBlocksType(selection, () => $createHeadingNode(type));
				}
			});
		}
		setShowBlockOptionsDropDown(false);
	};

	const formatQuote = () => {
		if (blockType !== 'quote') {
			editor.update(() => {
				const selection = $getSelection();

				if ($isRangeSelection(selection)) {
					$setBlocksType(selection, () => $createQuoteNode());
				}
			});
		}
		setShowBlockOptionsDropDown(false);
	};

	return (
		<div className='dropdown' ref={dropDownRef}>
			<Button type='text' className='item' icon={blockTypeToIcon.paragraph} onClick={formatParagraph}>
				<span className='text'>{blockTypeToBlockName.paragraph}</span>
				{blockType === 'paragraph' && <span className='active' />}
			</Button>
			<Button type='text' className='item' icon={blockTypeToIcon.h1} onClick={() => formatHeading('h1')}>
				<span className='text'>{blockTypeToBlockName.h1}</span>
				{blockType === 'h1' && <span className='active' />}
			</Button>
			<Button type='text' className='item' icon={blockTypeToIcon.h2} onClick={() => formatHeading('h2')}>
				<span className='text'>{blockTypeToBlockName.h2}</span>
				{blockType === 'h2' && <span className='active' />}
			</Button>
			<Button type='text' className='item' icon={blockTypeToIcon.quote} onClick={formatQuote}>
				<span className='text'>{blockTypeToBlockName.quote}</span>
				{blockType === 'quote' && <span className='active' />}
			</Button>
		</div>
	);
};

const ToolbarPlugin: React.FC = () => {
	const [editor] = useLexicalComposerContext();
	const toolbarRef = useRef(null);
	const [canUndo, setCanUndo] = useState(false);
	const [canRedo, setCanRedo] = useState(false);
	const [blockType, setBlockType] = useState('paragraph');
	const [showBlockOptionsDropDown, setShowBlockOptionsDropDown] = useState(false);
	const [isLink, setIsLink] = useState(false);
	const [isBold, setIsBold] = useState(false);
	const [isItalic, setIsItalic] = useState(false);
	const [isUnderline, setIsUnderline] = useState(false);
	const [isStrikethrough, setIsStrikethrough] = useState(false);
	const [isCode, setIsCode] = useState(false);

	const updateToolbar = useCallback(() => {
		const selection = $getSelection();
		if ($isRangeSelection(selection)) {
			const anchorNode = selection.anchor.getNode();
			const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
			const elementKey = element.getKey();
			const elementDOM = editor.getElementByKey(elementKey);
			if (elementDOM !== null) {
				if ($isListNode(element)) {
					const parentList = $getNearestNodeOfType(anchorNode, ListNode);
					const type = parentList ? parentList.getTag() : element.getTag();
					setBlockType(type);
				} else {
					const type = $isHeadingNode(element) ? element.getTag() : element.getType();
					setBlockType(type);
				}
			}
			// Update text format
			setIsBold(selection.hasFormat('bold'));
			setIsItalic(selection.hasFormat('italic'));
			setIsUnderline(selection.hasFormat('underline'));
			setIsStrikethrough(selection.hasFormat('strikethrough'));
			setIsCode(selection.hasFormat('code'));

			// Update links
			const node = getSelectedNode(selection);
			const parent = node?.getParent();
			if ($isLinkNode(parent) || $isLinkNode(node)) {
				setIsLink(true);
			} else {
				setIsLink(false);
			}
		}
	}, [editor]);

	useEffect(() => {
		return mergeRegister(
			editor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					updateToolbar();
				});
			}),
			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				() => {
					updateToolbar();
					return false;
				},
				LowPriority,
			),
			editor.registerCommand(
				CAN_UNDO_COMMAND,
				(payload) => {
					setCanUndo(payload);
					return false;
				},
				LowPriority,
			),
			editor.registerCommand(
				CAN_REDO_COMMAND,
				(payload) => {
					setCanRedo(payload);
					return false;
				},
				LowPriority,
			),
		);
	}, [editor, updateToolbar]);

	const insertLink = useCallback(() => {
		if (!isLink) {
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
		} else {
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
		}
	}, [editor, isLink]);

	const formatBulletList = () => {
		if (blockType !== 'ul') {
			editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
		} else {
			editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
		}
		setShowBlockOptionsDropDown(false);
	};

	const formatNumberedList = () => {
		if (blockType !== 'ol') {
			editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
		} else {
			editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
		}
		setShowBlockOptionsDropDown(false);
	};

	return (
		<Space size={6} role='presentation' className='toolbar' ref={toolbarRef} onClick={(e) => e.preventDefault()}>
			<Button
				type='text'
				className={toolbarItem}
				disabled={!canUndo}
				aria-label='Undo'
				tabIndex={-1}
				icon={<UndoOutlined />}
				onClick={() => {
					editor.dispatchCommand(UNDO_COMMAND, undefined);
				}}
			/>
			<Button
				type='text'
				tabIndex={-1}
				disabled={!canRedo}
				className={toolbarItem}
				aria-label='Redo'
				icon={<RedoOutlined />}
				onClick={() => {
					editor.dispatchCommand(REDO_COMMAND, undefined);
				}}
			/>
			<Divider type='vertical' />
			{supportedBlockTypes.has(blockType) && (
				<>
					<Button
						type='text'
						tabIndex={-1}
						className={toolbarItem}
						aria-label='Formatting Options'
						icon={blockTypeToIcon[blockType] || <MessageOutlined />}
						onClick={() => setShowBlockOptionsDropDown(!showBlockOptionsDropDown)}
					>
						<span className='text'>{_.get(blockTypeToBlockName, blockType)}</span>
						<DownOutlined />
					</Button>
					{showBlockOptionsDropDown &&
						createPortal(
							<BlockOptionsDropdownList
								editor={editor}
								blockType={blockType}
								toolbarRef={toolbarRef}
								setShowBlockOptionsDropDown={setShowBlockOptionsDropDown}
							/>,
							document.body,
						)}
					<Divider type='vertical' />
				</>
			)}
			<>
				<Button
					type='text'
					className={toolbarItem + (isBold ? active : '')}
					aria-label='Format Bold'
					tabIndex={-1}
					icon={<BoldOutlined />}
					onClick={() => {
						editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
					}}
				/>
				<Button
					type='text'
					className={toolbarItem + (isItalic ? active : '')}
					aria-label='Format Italics'
					tabIndex={-1}
					icon={<ItalicOutlined />}
					onClick={() => {
						editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
					}}
				/>
				<Button
					type='text'
					className={toolbarItem + (isUnderline ? active : '')}
					aria-label='Format Underline'
					tabIndex={-1}
					icon={<UnderlineOutlined />}
					onClick={() => {
						editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
					}}
				/>
				<Button
					type='text'
					className={toolbarItem + (isStrikethrough ? active : '')}
					aria-label='Format Strikethrough'
					tabIndex={-1}
					icon={<StrikethroughOutlined />}
					onClick={() => {
						editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
					}}
				/>
				<Button
					type='text'
					aria-label='Format bullet'
					className={toolbarItem + (blockType === 'ul' ? active : '')}
					tabIndex={-1}
					icon={<UnorderedListOutlined />}
					onClick={formatBulletList}
				/>
				<Button
					type='text'
					aria-label='Format Number'
					className={toolbarItem + (blockType === 'ol' ? active : '')}
					tabIndex={-1}
					icon={<OrderedListOutlined />}
					onClick={formatNumberedList}
				/>
				<Button
					type='text'
					className={toolbarItem + (isCode ? active : '')}
					aria-label='Insert Code'
					tabIndex={-1}
					icon={<CodeOutlined />}
					onClick={() => {
						editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
					}}
				/>
				<Button
					type='text'
					className={toolbarItem + (isLink ? active : '')}
					aria-label='Insert Link'
					tabIndex={-1}
					icon={<LinkOutlined />}
					onClick={insertLink}
				/>
				{isLink &&
					createPortal(
						<FloatingLinkEditor editor={editor} anchorElem={editor.getRootElement() || document.body} />,
						document.body,
					)}
				<Divider type='vertical' />
				<Button
					type='text'
					className={toolbarItem}
					aria-label='Left Align'
					tabIndex={-1}
					icon={<AlignLeftOutlined />}
					onClick={() => {
						editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
					}}
				/>
				<Button
					type='text'
					className={toolbarItem}
					aria-label='Center Align'
					tabIndex={-1}
					icon={<AlignCenterOutlined />}
					onClick={() => {
						editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
					}}
				/>
				<Button
					type='text'
					className={toolbarItem}
					aria-label='Right Align'
					tabIndex={-1}
					icon={<AlignRightOutlined />}
					onClick={() => {
						editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
					}}
				/>
				<Button
					type='text'
					className={toolbarItem}
					aria-label='Justify Align'
					tabIndex={-1}
					icon={<MenuOutlined />}
					onClick={() => {
						editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
					}}
				/>
			</>
		</Space>
	);
};

export default ToolbarPlugin;
